import { useState } from "react";
import { Link } from "react-router-dom";

const topPicks = [
  {
    id: 1,
    name: "Compact Linen Sofa in Oat",
    price: "$1,290",
    tag: "Editor's Pick",
    image: "https://public.readdy.ai/ai/img_res/5e8d344ffe0c1e911285bed1a36bf412.jpg",
    note: "Two-seater with visible legs — perfect for small living rooms.",
  },
  {
    id: 2,
    name: "Round Oak Coffee Table",
    price: "$420",
    tag: "Space-Saving",
    image: "https://readdy.ai/api/search-image?query=round%20light%20oak%20coffee%20table%20minimal%20clean%20design%20solid%20wood%20product%20photography%20simple%20white%20background%20warm%20tone&width=600&height=600&seq=san_shop2&orientation=squarish",
    note: "No sharp corners, keeps the floor feeling open.",
  },
  {
    id: 3,
    name: "Large Ivory Wool Rug",
    price: "$680",
    tag: "Go Large",
    image: "https://readdy.ai/api/search-image?query=large%20ivory%20wool%20area%20rug%20warm%20neutral%20texture%20minimal%20clean%20product%20photography%20simple%20white%20background%20soft%20pile&width=600&height=600&seq=san_shop3&orientation=squarish",
    note: "Size up — a large rug makes the whole room feel bigger.",
  },
];

const tiers = [
  {
    label: "Budget",
    range: "Under $300",
    items: [
      { name: "IKEA KALLAX Shelf Unit", price: "$129", note: "Vertical storage that draws the eye up." },
      { name: "H&M Home Linen Cushion Cover", price: "$18", note: "Warm oat tone, natural texture." },
      { name: "Sheer Linen Curtain Panel", price: "$45", note: "Lets in maximum light, adds softness." },
    ],
  },
  {
    label: "Mid-Range",
    range: "$300 – $900",
    items: [
      { name: "West Elm Slope Armchair", price: "$599", note: "Low profile, visible legs, neutral upholstery." },
      { name: "Article Culla Coffee Table", price: "$449", note: "Round, light oak, compact footprint." },
      { name: "Boucle Throw Blanket", price: "$89", note: "Adds texture without adding visual weight." },
    ],
  },
  {
    label: "Luxury",
    range: "$900+",
    items: [
      { name: "Muuto Outline Sofa", price: "$3,200", note: "Slim profile, Scandinavian design, perfect for small spaces." },
      { name: "Menu Plinth Side Table", price: "$420", note: "Travertine top, minimal footprint." },
      { name: "Beni Ourain Rug", price: "$1,100", note: "Handwoven, warm ivory, adds artisan depth." },
    ],
  },
];

const faqs = [
  {
    q: "What's the best sofa size for a small apartment?",
    a: "A two-seater or compact three-seater (under 200cm wide) works best. Look for sofas with visible legs — they make the floor feel continuous and the room feel larger. Avoid anything with a high back or heavy base.",
  },
  {
    q: "Should I use a rug in a small living room?",
    a: "Yes — but go larger than you think. A rug that's too small makes a room feel fragmented. Ideally, the front legs of all your seating should sit on the rug. A large, light-toned rug in ivory or oat will make the floor feel expansive.",
  },
  {
    q: "How do I add personality without making the space feel cluttered?",
    a: "Texture is your best tool. Mix linen, boucle, natural wood, and ceramic in similar tones. The variation in material creates interest without colour contrast. Limit decorative objects to three or four pieces you genuinely love.",
  },
  {
    q: "What colours work best with soft neutrals in a small space?",
    a: "Warm whites, cream, oat, sand, and warm ivory are the core palette. If you want to introduce a subtle accent, try a muted terracotta or warm sage — both sit harmoniously with neutrals without disrupting the calm.",
  },
  {
    q: "How do I make low ceilings feel higher?",
    a: "Use vertical lines — tall, slim shelving, floor-to-ceiling curtains hung close to the ceiling, and artwork hung slightly higher than usual. Keep furniture low-profile so the upper half of the room stays open and airy.",
  },
];

export default function SANShopSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className="py-24 bg-[#FAF7F0]">
      <div className="max-w-screen-xl mx-auto px-8 md:px-16">

        {/* Top Picks */}
        <div className="mb-20">
          <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            Our Recommendations
          </p>
          <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-12" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            Top Picks for a Small Neutral Living Room
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {topPicks.map((item) => (
              <div key={item.id} className="group bg-white rounded-2xl overflow-hidden">
                <div className="w-full aspect-square overflow-hidden bg-[#F0EBE1]">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-[#EDE6DA] text-[#6B5E53] text-[9px] px-3 py-1 rounded-full tracking-[0.18em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {item.tag}
                  </span>
                  <h3 className="text-[#2C2825] text-base mb-1" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
                    {item.name}
                  </h3>
                  <p className="text-[#8B8378] text-xs leading-relaxed mb-3" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                    {item.note}
                  </p>
                  <p className="text-[#2C2825] text-sm" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
                    {item.price}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Budget Tiers */}
        <div className="mb-20">
          <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-10" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            Shop by Budget
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier) => (
              <div key={tier.label} className="bg-white rounded-2xl p-8">
                <p className="text-[#B5A898] text-[10px] tracking-[0.35em] uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {tier.label}
                </p>
                <p className="text-[#2C2825] text-xl mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
                  {tier.range}
                </p>
                <div className="space-y-5">
                  {tier.items.map((item) => (
                    <div key={item.name} className="border-b border-[#EDE6DA] pb-5 last:border-0 last:pb-0">
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-[#2C2825] text-sm" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
                          {item.name}
                        </p>
                        <span className="text-[#6B5E53] text-sm ml-4 flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {item.price}
                        </span>
                      </div>
                      <p className="text-[#8B8378] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                        {item.note}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Complete the Look */}
        <div className="mb-20 bg-white rounded-2xl p-10 md:p-14">
          <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-3" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            Complete the Look
          </h2>
          <p className="text-[#8B8378] text-sm mb-10" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
            Every piece in a small apartment should work together. Here's a full room pairing:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Sofa", desc: "Compact linen, oat tone", link: "/cozy-corners/best-beige-sofas" },
              { label: "Rug", desc: "Large ivory wool, 200×300cm", link: "/products/neutral-rugs" },
              { label: "Coffee Table", desc: "Round light oak, 80cm", link: "/products/wood-coffee-tables" },
              { label: "Curtains", desc: "Sheer linen, floor-length", link: "/products/linen-curtains" },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.link}
                className="group block bg-[#FAF7F0] rounded-xl p-5 hover:bg-[#EDE6DA] transition-colors duration-300 cursor-pointer"
              >
                <p className="text-[#2C2825] text-sm mb-1 group-hover:text-[#2C2825]" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
                  {item.label}
                </p>
                <p className="text-[#8B8378] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                  {item.desc}
                </p>
                <span className="inline-flex items-center gap-1 text-[#B5A898] text-[10px] tracking-[0.15em] mt-3 group-hover:text-[#6B5E53] transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                  View Guide <i className="ri-arrow-right-line text-[10px]" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-10" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-8 py-6 text-left cursor-pointer"
                >
                  <span className="text-[#2C2825] text-sm pr-8" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
                    {faq.q}
                  </span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${openFaq === i ? "subtract" : "add"}-line text-[#B5A898] text-base`} />
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-8 pb-6">
                    <p className="text-[#6B5E53] text-sm leading-[1.85]" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
